import { useSyncExternalStore } from 'react';

/**
 * The width below which the app draws its mobile layout — bottom tabs, bottom
 * sheets, a sticky booking bar.
 *
 * 767px is one below Tailwind's `md`, so the CSS breakpoints and the structural
 * branches switch at the same width. A layout that changed shape at one width
 * and swapped components at another would have a band where it is neither.
 *
 * There is no separate mobile build (ADR-0002, ADR-0010): the Capacitor and
 * Tauri shells load the same bundle, so narrowing a browser window is a
 * faithful preview of what a phone draws.
 */
export const MOBILE_LAYOUT_QUERY = '(max-width: 767px)';

function subscribe(onChange: () => void): () => void {
  const query = window.matchMedia(MOBILE_LAYOUT_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function getSnapshot(): boolean {
  return window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
}

/** Prerendered HTML has no viewport, so it is built at the desktop layout. */
function getServerSnapshot(): boolean {
  return false;
}

/**
 * Whether this viewport gets the mobile layout.
 *
 * `useSyncExternalStore` rather than an effect: an effect would render the
 * desktop layout first and correct it after paint, which on a phone is a
 * visible flash of a 1280px header every time the app starts.
 *
 * Most of the mobile layout is plain CSS (`max-md:` / `md:`). This hook is for
 * the places where the two layouts are genuinely different component trees —
 * the search screen's map-and-sheet, the ride screen's sticky bar — where
 * rendering both and hiding one would put two copies of every ride, and two
 * booking buttons, in the accessibility tree.
 */
export function useIsMobileLayout(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
